<template>
  <teleport to="body">
    <div
      ref="layerRef"
      class="parallax-bg"
      :class="{ 'parallax-bg--hidden': hiddenByOptOut }"
      aria-hidden="true"
    >
      <img
        v-for="item in shapes"
        :key="item.id"
        :src="item.src"
        class="parallax-shape"
        :style="item.cssStyle"
        alt=""
        @error="handleImgError(item)"
      />
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { splashBackground } from '@/config/platformAssets'

const SPEED = 0.12

const layerRef = ref<HTMLElement | null>(null)

const sources = {
  blue:   { primary: splashBackground('splash-blue.png'),   fallback: splashBackground('blue-top.png') },
  green:  { primary: splashBackground('splash-green.png'),  fallback: splashBackground('green-left.png') },
  orange: { primary: splashBackground('spalsh-oranage.png'), fallback: splashBackground('orange-bottom.png') },
  purple: { primary: splashBackground('splash-purple.png'), fallback: splashBackground('purple-top.png') },
} as const

type CK = keyof typeof sources

interface Shape { id: string; src: string; color: CK; fallbackUsed: boolean; cssStyle: Record<string, string> }

const defs: Array<{ color: CK; top: string; left?: string; right?: string; size: string; rotate: string; flip?: boolean }> = [
  { color: 'green',  top: '110vh',  left: '0vw',  size: 'clamp(180px,22vw,380px)', rotate: '15deg' },
  { color: 'purple', top: '135vh',  right: '0vw', size: 'clamp(160px,20vw,340px)', rotate: '-10deg' },
  { color: 'orange', top: '220vh',  left: '1vw',  size: 'clamp(200px,24vw,400px)', rotate: '8deg',   flip: true },
  { color: 'blue',   top: '260vh',  right: '0vw', size: 'clamp(180px,22vw,360px)', rotate: '-18deg' },
  { color: 'green',  top: '340vh',  right: '1vw', size: 'clamp(170px,20vw,340px)', rotate: '22deg',  flip: true },
  { color: 'purple', top: '380vh',  left: '0vw',  size: 'clamp(190px,22vw,370px)', rotate: '-12deg' },
  { color: 'orange', top: '460vh',  right: '0vw', size: 'clamp(160px,18vw,320px)', rotate: '5deg' },
  { color: 'blue',   top: '510vh',  left: '1vw',  size: 'clamp(200px,24vw,400px)', rotate: '-20deg', flip: true },
  { color: 'green',  top: '590vh',  left: '0vw',  size: 'clamp(180px,22vw,360px)', rotate: '192deg' },
  { color: 'purple', top: '640vh',  right: '1vw', size: 'clamp(170px,20vw,340px)', rotate: '-8deg',  flip: true },
  { color: 'orange', top: '720vh',  right: '0vw', size: 'clamp(190px,22vw,380px)', rotate: '18deg' },
  { color: 'blue',   top: '780vh',  left: '0vw',  size: 'clamp(160px,18vw,320px)', rotate: '-15deg' },
]

const shapes = reactive<Shape[]>(
  defs.map((d, i) => ({
    id: `s${i}`,
    src: sources[d.color].primary,
    color: d.color,
    fallbackUsed: false,
    cssStyle: {
      top: d.top,
      ...(d.left != null ? { left: d.left } : {}),
      ...(d.right != null ? { right: d.right } : {}),
      width: d.size,
      transform: `rotate(${d.rotate})${d.flip ? ' scaleX(-1)' : ''}`,
    },
  }))
)

const handleImgError = (item: Shape) => {
  if (!item.fallbackUsed) {
    item.src = sources[item.color].fallback
    item.fallbackUsed = true
  }
}

let raf = 0
const tick = () => {
  raf = 0
  if (!layerRef.value) return
  const y = window.scrollY
  layerRef.value.style.transform = `translate3d(0,${-y * SPEED}px,0)`
}
const onScroll = () => { if (!raf) raf = requestAnimationFrame(tick) }

const hiddenByOptOut = ref(false)
const intersecting = new Set<Element>()
let optOutObserver: IntersectionObserver | null = null
let optOutMutationObserver: MutationObserver | null = null

const syncHiddenState = () => {
  hiddenByOptOut.value = intersecting.size > 0
}

const observeOptOutTargets = () => {
  if (!optOutObserver) return
  const targets = document.querySelectorAll('[data-no-parallax]')
  targets.forEach((el) => optOutObserver!.observe(el))
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  tick()

  if (typeof IntersectionObserver !== 'undefined') {
    optOutObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) intersecting.add(entry.target)
        else intersecting.delete(entry.target)
      }
      syncHiddenState()
    })
    observeOptOutTargets()

    if (typeof MutationObserver !== 'undefined') {
      optOutMutationObserver = new MutationObserver(() => observeOptOutTargets())
      optOutMutationObserver.observe(document.body, { childList: true, subtree: true })
    }
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  if (raf) cancelAnimationFrame(raf)
  optOutObserver?.disconnect()
  optOutMutationObserver?.disconnect()
  intersecting.clear()
})
</script>

<style scoped>
.parallax-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 0;
  overflow: visible;
  pointer-events: none;
  z-index: 0;
  will-change: transform;
  transition: opacity 180ms ease;
}

.parallax-bg--hidden {
  opacity: 0;
}

.parallax-shape {
  position: absolute;
  height: auto;
  pointer-events: none;
  user-select: none;
  filter: brightness(1.1) saturate(1.3) contrast(1.12);
}

@media (max-width: 1024px) {
  .parallax-bg { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .parallax-bg { display: none; }
}
</style>
